import CryptoJS from 'crypto-js';

class PasswordHasher {
    /**
     * Generates a random salt
     * @param {number} length - Length of the salt (default: 16)
     * @returns {string} - Base64 encoded salt
     */
    static generateSalt(length = 16) {
        const randomBytes = CryptoJS.lib.WordArray.random(length);
        return CryptoJS.enc.Base64.stringify(randomBytes);
    }

    /**
     * Hashes password with salt using PBKDF2
     * @param {string} password - Plain text password
     * @param {string} salt - Salt to use for hashing
     * @param {number} iterations - Number of iterations (default: 10000)
     * @returns {string} - Base64 encoded hash
     */
    static hashPasswordWithSalt(password, salt, iterations = 10000) {
        const saltWordArray = CryptoJS.enc.Base64.parse(salt);
        const hash = CryptoJS.PBKDF2(password, saltWordArray, {
            keySize: 256/32, // 256 bits
            iterations: iterations,
            hasher: CryptoJS.algo.SHA256
        });
        return CryptoJS.enc.Base64.stringify(hash);
    }

    /**
     * Creates a salted hash with embedded salt (salt:hash format)
     * @param {string} password - Plain text password
     * @param {number} iterations - Number of iterations (default: 10000)
     * @returns {string} - salt:hash format
     */
    static createSaltedHash(password, iterations = 10000) {
        const salt = this.generateSalt();
        const hash = this.hashPasswordWithSalt(password, salt, iterations);
        return `${salt}:${hash}`;
    }

    /**
     * Simple SHA256 hash (for legacy compatibility if needed)
     * @param {string} password - Plain text password
     * @returns {string} - SHA256 hash in hex format
     */
    static simpleHash(password) {
        return CryptoJS.SHA256(password).toString(CryptoJS.enc.Hex);
    }

    /**
     * Hash password with username as salt (deterministic for same user)
     * @param {string} password - Plain text password
     * @param {string} username - Username to use as salt component
     * @returns {string} - Base64 encoded hash
     */
    static hashWithUsername(password, username) {
        const combined = `${username}:${password}`;
        const hash = CryptoJS.PBKDF2(combined, username, {
            keySize: 256/32,
            iterations: 5000,
            hasher: CryptoJS.algo.SHA256
        });
        return CryptoJS.enc.Base64.stringify(hash);
    }
}

export default PasswordHasher;
